/**
 * AC-H-3 — CMS 設備儀表板（唯讀）。
 *
 * 對應 BE GET /admin/devices。權限 admin.device_view（superadmin / ticketing_admin）。
 *
 * 設計（mockup 16-E1-devices.png）：
 *   - 4 KPI cards：在線 / 離線 >5min / 24h 錯誤總筆數 / 24h 平均錯誤/裝置
 *   - 裝置列表 table（device_code / 類型 / 狀態 / 最後 heartbeat / 24h 錯誤計數）
 *   - 24h 錯誤分布（by code，bar list；top 20）
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  createDevice,
  updateDevice,
  DEVICE_TYPES,
  getDeviceDashboard,
  type DeviceListItem,
  type DeviceErrorBucket,
} from '../lib/api-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 60000);
}

function deviceStatusPill(d: DeviceListItem): JSX.Element {
  const m = minutesSince(d.last_heartbeat);
  if (d.online && m !== null && m <= 5) return <Pill tone="success">在線</Pill>;
  if (d.online) return <Pill tone="warning">在線（heartbeat 逾時）</Pill>;
  if (m === null) return <Pill tone="neutral">未上線</Pill>;
  return <Pill tone="danger">離線 {m} 分鐘</Pill>;
}

export function DeviceDashboardPage(): JSX.Element {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.devices.dashboard'],
    queryFn: getDeviceDashboard,
    refetchInterval: 60_000, // 1 分鐘自動 refresh
  });

  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formCode, setFormCode] = useState('');
  const [formType, setFormType] = useState<string>('kiosk');
  const [formLocation, setFormLocation] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['admin.devices.dashboard'] });

  const createMutation = useMutation({
    mutationFn: () =>
      createDevice({
        device_code: formCode.trim(),
        device_type: formType,
        ...(formLocation.trim() !== '' ? { location: formLocation.trim() } : {}),
      }),
    onSuccess: () => {
      setShowForm(false);
      setFormCode('');
      setFormLocation('');
      setFormError(null);
      void invalidate();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : '新增失敗';
      setFormError(msg.includes('DEVICE_CODE_TAKEN') ? '此 device_code 已存在' : msg);
    },
  });

  const retireMutation = useMutation({
    mutationFn: (id: string) => updateDevice(id, { retired: true }),
    onSuccess: () => void invalidate(),
  });

  const devices: DeviceListItem[] = data?.devices ?? [];
  const errBuckets: DeviceErrorBucket[] = data?.error_distribution_24h ?? [];
  const errMax = errBuckets.reduce((m, b) => (b.count > m ? b.count : m), 0);

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">設備儀表板</h1>
        <div className="page-head__actions">
          <Button
            variant="primary"
            onClick={() => {
              setShowForm((v) => !v);
              setFormError(null);
            }}
          >
            {showForm ? '取消新增' : '＋ 新增設備'}
          </Button>
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        AC-H-3：每台裝置目前狀態 + 近 24h 錯誤分布，1 分鐘自動更新。檢視需{' '}
        <code>admin.device_view</code>；登錄 / 退役設備需 <code>admin.device_manage</code>
        （superadmin / ticketing_admin 預設持有）。設備生命週期：此處登錄 → 機台綁定該 device_code →
        heartbeat 上線。
      </p>

      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h2 style={{ fontSize: 15, margin: '0 0 12px 0' }}>登錄新設備</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
              device_code（唯一）
              <input
                value={formCode}
                onChange={(e) => {
                  setFormCode(e.target.value);
                  setFormError(null);
                }}
                placeholder="KIOSK-02"
                style={{
                  display: 'block',
                  marginTop: 4,
                  padding: '8px 10px',
                  fontFamily: 'monospace',
                  border: '1px solid var(--c-border)',
                  borderRadius: 6,
                  minWidth: 180,
                }}
              />
            </label>
            <label style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
              類型
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                style={{
                  display: 'block',
                  marginTop: 4,
                  padding: '8px 10px',
                  border: '1px solid var(--c-border)',
                  borderRadius: 6,
                }}
              >
                {DEVICE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
              位置（選填）
              <input
                value={formLocation}
                onChange={(e) => setFormLocation(e.target.value)}
                placeholder="二航廈大廳"
                style={{
                  display: 'block',
                  marginTop: 4,
                  padding: '8px 10px',
                  border: '1px solid var(--c-border)',
                  borderRadius: 6,
                  minWidth: 200,
                }}
              />
            </label>
            <Button
              variant="primary"
              disabled={formCode.trim() === '' || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? '登錄中…' : '登錄'}
            </Button>
          </div>
          {formError !== null && (
            <p style={{ marginTop: 10, color: 'var(--c-danger, #b91c1c)', fontWeight: 600 }}>
              ⚠ {formError}
            </p>
          )}
        </div>
      )}

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>提醒：此頁需 admin.device_view 權限。</div>
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div className="card">
              <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>在線設備</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>
                {data.kpis.online} / {data.kpis.total}
              </div>
            </div>
            <div className="card">
              <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>離線 &gt;5 分鐘</div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  color: data.kpis.offline_over_5min > 0 ? 'var(--c-danger, #b91c1c)' : undefined,
                }}
              >
                {data.kpis.offline_over_5min} 台
              </div>
            </div>
            <div className="card">
              <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>24h 錯誤總筆數</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{data.kpis.errors_24h_total} 件</div>
            </div>
            <div className="card">
              <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>平均 / 裝置</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>
                {data.kpis.total > 0
                  ? (data.kpis.errors_24h_total / data.kpis.total).toFixed(1)
                  : '0.0'}
              </div>
            </div>
          </div>

          {/* 雙欄：左裝置列表，右錯誤分布 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
              gap: 16,
            }}
          >
            <div>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>裝置列表（{devices.length} 台）</h2>
              {devices.length === 0 ? (
                <div className="table-card">
                  <table>
                    <tbody>
                      <tr>
                        <td className="empty-cell">尚無登錄之裝置</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-card">
                  <table>
                    <thead>
                      <tr>
                        <th>裝置代號</th>
                        <th>類型</th>
                        <th>位置</th>
                        <th>狀態</th>
                        <th>最後 heartbeat</th>
                        <th style={{ textAlign: 'right' }}>24h 錯誤</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map((d) => (
                        <tr key={d.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.device_code}</td>
                          <td>{d.device_type}</td>
                          <td>{d.location ?? '—'}</td>
                          <td>{deviceStatusPill(d)}</td>
                          <td style={{ fontSize: 12 }}>{formatDateTime(d.last_heartbeat)}</td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontFamily: 'monospace',
                              color: d.errors_24h > 0 ? 'var(--c-danger, #b91c1c)' : undefined,
                            }}
                          >
                            {d.errors_24h}
                          </td>
                          <td>
                            <button
                              type="button"
                              disabled={retireMutation.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `退役設備 ${d.device_code}？退役後該機台 heartbeat 將被拒、並從列表移除。`,
                                  )
                                ) {
                                  retireMutation.mutate(d.id);
                                }
                              }}
                              style={{
                                fontSize: 12,
                                padding: '4px 10px',
                                border: '1px solid var(--c-border)',
                                borderRadius: 6,
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'var(--c-danger, #b91c1c)',
                              }}
                            >
                              退役
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>24h 錯誤分布</h2>
              {errBuckets.length === 0 ? (
                <div className="card">
                  <span style={{ color: 'var(--c-text-muted)' }}>過去 24 小時無錯誤事件</span>
                </div>
              ) : (
                <div className="card">
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {errBuckets.map((b) => (
                      <li
                        key={b.code}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                      >
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            minWidth: 80,
                          }}
                        >
                          {b.code}
                        </span>
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            background: 'var(--c-bg-subtle, #f3f4f6)',
                            borderRadius: 4,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${errMax > 0 ? (b.count / errMax) * 100 : 0}%`,
                              height: '100%',
                              background: 'var(--c-danger, #b91c1c)',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            minWidth: 40,
                            textAlign: 'right',
                          }}
                        >
                          {b.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
